import type { Socket } from 'socket.io-client';

export type SocketNamespace = 'default' | 'chats';

export type SocketSessionAuthPayload = {
    token: string;
    sysOrgId?: string;
    sysServiceId?: string;
    reason?: 'initial' | 'refresh' | 'retry';
};

export type SocketSessionState =
    | 'idle'
    | 'connecting'
    | 'connected'
    | 'authenticating'
    | 'authorized'
    | 'auth_failed'
    | 'disconnected';

type LocalSessionEvent = 'authorized' | 'unauthorized' | 'session_state' | 'auth_failed';

type SocketSessionLocalEvents = {
    authorized: (payload: any) => void;
    unauthorized: (payload?: any) => void;
    session_state: (state: SocketSessionState) => void;
    auth_failed: (error: any) => void;
};

type EmitPolicy = 'wait-auth' | 'reject-if-unauthorized' | 'drop-if-unauthorized';

type EmitWithAckOptions = {
    timeout?: number;
    volatile?: boolean;
    policy?: EmitPolicy;
};

type AnyFn = (...args: any[]) => void;

const LOCAL_EVENTS = new Set<LocalSessionEvent>(['authorized', 'unauthorized', 'session_state', 'auth_failed']);

export class SocketSession {
    private authPayload: SocketSessionAuthPayload | null = null;
    private stateValue: SocketSessionState = 'idle';
    private authPromise: Promise<void> | null = null;
    private connectPromise: Promise<void> | null = null;
    private authVersionValue = 0;
    private authFingerprint: string | null = null;
    private localListeners = new Map<LocalSessionEvent, Set<AnyFn>>();

    constructor(
        private readonly socket: Socket,
        public readonly namespace: SocketNamespace
    ) {
        this.socket.on('connect', this.handleConnect);
        this.socket.on('disconnect', this.handleDisconnect);
        this.socket.on('connect_error', this.handleConnectError);
        this.socket.on('auth_revoked', this.handleAuthRevoked);
    }

    get connected() {
        return this.socket.connected;
    }

    get active() {
        return this.socket.active;
    }

    get id() {
        return this.socket.id;
    }

    get state() {
        return this.stateValue;
    }

    get authorized() {
        return this.stateValue === 'authorized';
    }

    get authVersion() {
        return this.authVersionValue;
    }

    get raw() {
        return this.socket;
    }

    setAuthPayload(payload: SocketSessionAuthPayload | null) {
        console.log('SocketAuth', 'setAuthPayload', payload);
        this.authPayload = payload;
        if (!payload) {
            this.authVersionValue = 0;
            this.authFingerprint = null;
            if (this.connected) {
                this.setState('connected');
            } else {
                this.setState('idle');
            }
            return;
        }
        if (this.connected && this.makeAuthFingerprint() !== this.authFingerprint) {
            void this.authenticate().catch(() => undefined);
        }
    }

    connect() {
        if (!this.socket.connected && !this.socket.active) {
            this.setState('connecting');
        }
        this.socket.connect();
        return this;
    }

    disconnect() {
        this.authPromise = null;
        this.connectPromise = null;
        this.authVersionValue = 0;
        this.authFingerprint = null;
        this.setState('disconnected');
        this.socket.disconnect();
        return this;
    }

    on<E extends LocalSessionEvent>(event: E, handler: SocketSessionLocalEvents[E]): this;
    on(event: string, handler: AnyFn): this;
    on(event: string, handler: AnyFn): this {
        if (this.isLocalEvent(event)) {
            const listeners = this.localListeners.get(event) || new Set<AnyFn>();
            listeners.add(handler);
            this.localListeners.set(event, listeners);
            return this;
        }
        this.socket.on(event, handler);
        return this;
    }

    off<E extends LocalSessionEvent>(event: E, handler?: SocketSessionLocalEvents[E]): this;
    off(event: string, handler?: AnyFn): this;
    off(event: string, handler?: AnyFn): this {
        if (this.isLocalEvent(event)) {
            if (!handler) {
                this.localListeners.delete(event);
                return this;
            }
            this.localListeners.get(event)?.delete(handler);
            return this;
        }
        if (handler) {
            this.socket.off(event, handler);
        } else {
            this.socket.off(event);
        }
        return this;
    }

    once<E extends LocalSessionEvent>(event: E, handler: SocketSessionLocalEvents[E]): this;
    once(event: string, handler: AnyFn): this;
    once(event: string, handler: AnyFn): this {
        if (this.isLocalEvent(event)) {
            const onceHandler = (...args: any[]) => {
                this.off(event, onceHandler);
                handler(...args);
            };
            return this.on(event, onceHandler);
        }
        this.socket.once(event, handler);
        return this;
    }

    emit(event: string, ...args: any[]) {
        this.socket.emit(event, ...args);
        return this;
    }

    async ready() {
        if (!this.authPayload?.token) {
            throw new Error('Socket auth payload is not set');
        }
        if (!this.connected) {
            await this.waitForConnect();
        }
        if (this.authorized && this.makeAuthFingerprint() === this.authFingerprint) {
            return;
        }
        await this.authenticate();
    }

    async emitWithAck<T = any>(event: string, args: any[] = [], options: EmitWithAckOptions = {}): Promise<T> {
        const { timeout = 5000, volatile = false, policy = 'wait-auth' } = options;
        if (policy === 'drop-if-unauthorized' && !this.authorized) {
            return undefined as T;
        }
        if (policy === 'reject-if-unauthorized' && !this.authorized) {
            throw new Error('Socket is not authorized');
        }
        if (policy === 'wait-auth') {
            await this.ready();
        }

        return new Promise<T>((resolve, reject) => {
            let sendSocket = this.socket.timeout(timeout);
            if (volatile) {
                sendSocket = sendSocket.volatile;
            }
            sendSocket.emit(event, ...(args ?? []), (timeoutError: Error | null, response: any) => {
                if (timeoutError) {
                    reject(timeoutError);
                    return;
                }
                try {
                    resolve(this.normalizeAckResponse<T>(response));
                } catch (error) {
                    reject(error);
                }
            });
        });
    }

    emitWhenAuthorized(event: string, ...args: any[]) {
        void this.ready()
            .then(() => {
                this.socket.emit(event, ...args);
            })
            .catch(() => undefined);
        return this;
    }

    private async authenticate() {
        if (!this.authPayload?.token) {
            throw new Error('Socket auth payload is not set');
        }
        const nextFingerprint = this.makeAuthFingerprint();
        if (this.authorized && nextFingerprint === this.authFingerprint) {
            return;
        }
        if (this.authPromise) {
            return this.authPromise;
        }
        if (!this.connected) {
            await this.waitForConnect();
        }

        this.setState('authenticating');
        this.authPromise = this.emitWithAuthAck()
            .then((response) => {
                this.authVersionValue = Number(response?.authVersion || this.authVersionValue + 1);
                this.authFingerprint = nextFingerprint;
                this.setState('authorized');
                this.emitLocal('authorized', response);
            })
            .catch((error) => {
                this.authFingerprint = null;
                this.setState('auth_failed');
                this.emitLocal('auth_failed', error);
                this.emitLocal('unauthorized', error);
                throw error;
            })
            .finally(() => {
                this.authPromise = null;
            });
        return this.authPromise;
    }

    private emitWithAuthAck(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.socket
                .timeout(10000)
                .emit('authenticate', this.authPayload, (timeoutError: Error | null, response: any) => {
                    if (timeoutError) {
                        reject(timeoutError);
                        return;
                    }
                    try {
                        resolve(this.normalizeAckResponse(response));
                    } catch (error) {
                        reject(error);
                    }
                });
        });
    }

    private waitForConnect() {
        if (this.connected) {
            return Promise.resolve();
        }
        if (this.connectPromise) {
            return this.connectPromise;
        }

        this.setState('connecting');
        this.connectPromise = new Promise<void>((resolve, reject) => {
            const onConnect = () => {
                cleanup();
                resolve();
            };
            const onError = (error: any) => {
                cleanup();
                reject(error instanceof Error ? error : new Error(error?.message || 'Socket connection error'));
            };
            const cleanup = () => {
                this.socket.off('connect', onConnect);
                this.socket.off('connect_error', onError);
                this.connectPromise = null;
            };
            this.socket.on('connect', onConnect);
            this.socket.on('connect_error', onError);
            this.socket.connect();
        });

        return this.connectPromise;
    }

    private handleConnect = () => {
        this.setState('connected');
        if (this.authPayload?.token) {
            void this.authenticate().catch(() => undefined);
        }
    };

    private handleDisconnect = (reason?: any) => {
        const wasAuthorized = this.authorized;
        this.authPromise = null;
        this.connectPromise = null;
        this.authVersionValue = 0;
        this.authFingerprint = null;
        this.setState('disconnected');
        if (wasAuthorized) {
            this.emitLocal('unauthorized', reason);
        }
    };

    private handleConnectError = (error: any) => {
        this.setState('disconnected');
        this.emitLocal('auth_failed', error);
    };

    private handleAuthRevoked = (payload?: any) => {
        this.authPromise = null;
        this.authVersionValue = 0;
        this.authFingerprint = null;
        this.setState('connected');
        this.emitLocal('unauthorized', payload);
    };

    private normalizeAckResponse<T = any>(response: any): T {
        if (Array.isArray(response) && response.length === 2) {
            const [error, result] = response;
            if (error) {
                throw this.normalizeError(error);
            }
            return result as T;
        }
        if (response == null) {
            throw new Error('Пустой ответ от socket сервера');
        }
        if (response?.isError) {
            throw this.normalizeError(response);
        }
        return response as T;
    }

    private normalizeError(error: any) {
        if (error instanceof Error) {
            return error;
        }
        const normalized = new Error(error?.message || 'Неизвестная ошибка сокета');
        Object.assign(normalized, error);
        return normalized;
    }

    private makeAuthFingerprint() {
        return JSON.stringify({
            token: this.authPayload?.token || '',
            sysOrgId: this.authPayload?.sysOrgId || null,
            sysServiceId: this.authPayload?.sysServiceId || null,
        });
    }

    private emitLocal<E extends LocalSessionEvent>(event: E, ...args: Parameters<SocketSessionLocalEvents[E]>) {
        for (const listener of this.localListeners.get(event) || []) {
            listener(...args);
        }
    }

    private setState(state: SocketSessionState) {
        if (this.stateValue === state) {
            return;
        }
        this.stateValue = state;
        this.emitLocal('session_state', state);
    }

    private isLocalEvent(event: string): event is LocalSessionEvent {
        return LOCAL_EVENTS.has(event as LocalSessionEvent);
    }
}
